import { useState } from 'react'

// POS void reason labels — keep in sync with the worker's `VOID_REASON_CODES`
// (`backend/worker/src/routes/admin/orders.ts`). Used by both the Orders page
// detail drawer (mixed delivery + POS feed) and the dedicated Sales (POS)
// page; extracted here so a label-set change lands in one place.
export const POS_VOID_REASON_LABELS: Record<string, string> = {
  wrong_item: 'Wrong item',
  wrong_price: 'Wrong price',
  customer_changed_mind: 'Customer changed mind',
  damaged: 'Damaged',
  other: 'Other',
}

interface Props {
  orderNumber: string
  isSubmitting: boolean
  errorMessage: string | null
  onCancel: () => void
  onConfirm: (code: string, text?: string) => void
}

/// Modal for voiding a POS sale. 5 reason chips + free-text input that
/// appears only when 'Other' is selected. Submit button gates on a reason
/// being picked + (for Other) at least 3 chars of free text.
///
/// Calls `POST /admin/orders/:id/void-pos` via the parent's onConfirm
/// handler. The worker route runs an atomic batch: status → cancelled,
/// payment_status → refunded, restock products, reverse drops + loyalty +
/// store credit + gift-card balance, decrement discount-code usage, audit-
/// log as `pos_sale.void` with before/after JSON. See
/// `backend/worker/src/routes/admin/orders.ts` for the full reversal.
export function PosVoidModal({ orderNumber, isSubmitting, errorMessage, onCancel, onConfirm }: Props) {
  const [code, setCode] = useState<string | null>(null)
  const [text, setText] = useState('')
  const submitDisabled =
    isSubmitting ||
    code === null ||
    (code === 'other' && text.trim().length < 3)

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onCancel} />
      <div className="relative w-full max-w-md rounded-lg border border-[#2e2e2e] bg-[#1a1a1a] shadow-2xl">
        <div className="px-5 py-4 border-b border-[#2e2e2e]">
          <p className="text-white font-semibold">Void sale {orderNumber}</p>
          <p className="text-gray-500 text-xs mt-1">
            This restocks the products, reverses any drops + loyalty + store-credit, and rolls back the discount-code usage. The customer's wallet balance is refunded; the action is audit-logged.
          </p>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Reason</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(POS_VOID_REASON_LABELS).map(([k, label]) => {
                const selected = code === k
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setCode(k)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      selected
                        ? 'bg-red-500/20 border-red-500/60 text-red-200'
                        : 'bg-white/5 border-white/15 text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
          {code === 'other' && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Details</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                placeholder="Describe the reason (required)"
                className="mt-1 w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-red-500/60"
              />
            </div>
          )}
          {errorMessage && (
            <p className="text-xs text-red-400">{errorMessage}</p>
          )}
        </div>
        <div className="px-5 py-3 border-t border-[#2e2e2e] flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-gray-300 hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(code!, code === 'other' ? text.trim() : undefined)}
            disabled={submitDisabled}
            className="px-3 py-1.5 rounded-md bg-red-500/20 border border-red-500/60 text-xs font-semibold text-red-100 hover:bg-red-500/30 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Voiding…' : 'Void sale'}
          </button>
        </div>
      </div>
    </div>
  )
}
